import React, { useState } from 'react';
import { useTranslation } from '../../i18n';

type ViewState = 'menu' | 'limits' | 'closure' | 'history';

export const AccountManagementLimits: React.FC = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewState>('menu');
  const [historyTab, setHistoryTab] = useState<'lugas' | 'sports'>('lugas');

  const renderMenu = () => (
    <div className="flex flex-col gap-4">
      {/* Banner */}
      <div className="bg-[#fffdf0] border border-[#f5e3ba] rounded-lg p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h4 className="font-bold text-[15px] text-gray-900">{t('account.limits.restrictedSoon')}</h4>
          <p className="text-[14px] text-gray-600 mt-1">{t('account.limits.restrictedSoonDesc')}</p>
        </div>
        <button className="bg-[#222] hover:bg-black text-white font-bold text-[13px] px-6 py-2.5 rounded shadow-sm shrink-0 transition-colors">
          {t('account.limits.verifyNow')}
        </button>
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-2">{t('account.limits.menuTitle')}</h3>

      {/* Menu Cards */}
      <div className="flex flex-col gap-3">
        <button onClick={() => setView('limits')} className="bg-white hover:bg-gray-50 border border-gray-200 rounded-xl p-5 flex items-center justify-between text-left transition-colors cursor-pointer group">
          <div className="flex flex-col gap-1.5 pr-4">
            <span className="font-bold text-[15px] text-gray-900">{t('account.limits.limits')}</span>
            <span className="text-[14px] text-gray-500 leading-snug">{t('account.limits.limitsDesc')}</span>
          </div>
          <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>

        <button onClick={() => setView('closure')} className="bg-white hover:bg-gray-50 border border-gray-200 rounded-xl p-5 flex items-center justify-between text-left transition-colors cursor-pointer group">
          <div className="flex flex-col gap-1.5 pr-4">
            <span className="font-bold text-[15px] text-gray-900">{t('account.limits.closure')}</span>
            <span className="text-[14px] text-gray-500 leading-snug">{t('account.limits.closureDesc')}</span>
          </div>
          <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>

        <button onClick={() => setView('history')} className="bg-white hover:bg-gray-50 border border-gray-200 rounded-xl p-5 flex items-center justify-between text-left transition-colors cursor-pointer group">
          <div className="flex flex-col gap-1.5 pr-4">
            <span className="font-bold text-[15px] text-gray-900">{t('account.limits.history')}</span>
            <span className="text-[14px] text-gray-500 leading-snug">{t('account.limits.historyDesc')}</span>
          </div>
          <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
    </div>
  );

  const renderHeader = (titleKey: string) => (
    <div className="flex items-center gap-3 mb-6">
      <button onClick={() => setView('menu')} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
        <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
      </button>
      <h3 className="text-xl font-bold text-gray-900">{t(titleKey)}</h3>
    </div>
  );

  const renderLimits = () => (
    <div className="flex flex-col">
      {renderHeader('account.limits.myLimits')}

      {/* LUGAS Card */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
        <h4 className="font-bold text-[16px] text-gray-900 mb-4">{t('account.limits.lugasDeposit')}</h4>
        <p className="text-[14px] text-gray-800 mb-6 font-medium">
          {t('account.limits.lugasDepositDesc', { date: '25.08.2026', time: '00:22' })}
        </p>
        
        <div className="border border-gray-200 rounded-lg p-4 flex flex-col items-center justify-center mb-4 bg-gray-50">
          <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t('account.limits.crossOperator')}</span>
          <span className="text-[20px] font-black text-gray-900">100 €</span>
        </div>

        <button className="w-full bg-[#222] hover:bg-black text-white font-bold text-[14px] py-3.5 rounded-lg shadow-sm transition-colors mb-4 cursor-pointer">
          {t('account.limits.changeLimit')}
        </button>

        <p className="text-[12px] text-gray-400 leading-snug">
          {t('account.limits.lugasHint')}
        </p>
      </div>

      {/* Tipico Limits */}
      <button className="bg-white hover:bg-gray-50 border border-gray-200 rounded-xl p-5 flex items-center justify-between text-left transition-colors cursor-pointer group shadow-sm">
        <div className="flex flex-col gap-1 pr-4">
          <span className="font-bold text-[15px] text-gray-900">{t('account.limits.tipicoLimits')}</span>
          <span className="text-[14px] text-gray-500">{t('account.limits.tipicoLimitsDesc')}</span>
        </div>
        <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>
    </div>
  );

  const renderClosure = () => (
    <div className="flex flex-col">
      {renderHeader('account.limits.closureTitle')}

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <p className="text-[14px] text-gray-700 mb-6">{t('account.limits.closureInfo')}</p>

        <ul className="list-disc pl-5 flex flex-col gap-3 text-[14px] text-gray-700 mb-8 marker:text-gray-400">
          <li>
            <strong className="text-gray-900">{t('account.limits.closurePermTitle')}</strong> {t('account.limits.closurePermDesc')}
            <a href="#" className="text-[#0066cc] hover:underline font-medium">{t('account.limits.closurePermSupport')}</a>.
          </li>
          <li>
            <strong className="text-gray-900">{t('account.limits.closureTempTitle')}</strong> {t('account.limits.closureTempDesc')}
          </li>
          <li>
            <strong className="text-gray-900">{t('account.limits.closureCrossTitle')}</strong> {t('account.limits.closureCrossDesc')}
            <a href="#" className="text-[#0066cc] hover:underline font-medium">{t('account.limits.closureCrossOpt')}</a>.
          </li>
        </ul>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-bold text-gray-900">{t('account.limits.timePeriod')}</label>
            <div className="relative">
              <select className="w-full appearance-none bg-gray-50 border border-gray-200 rounded p-3.5 pr-10 text-[14px] text-gray-700 focus:outline-none focus:border-tipico-red transition-colors cursor-pointer">
                <option value="">{t('account.limits.chooseOption')}</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-bold text-gray-900">{t('account.limits.reason')}</label>
            <div className="relative">
              <select className="w-full appearance-none bg-gray-50 border border-gray-200 rounded p-3.5 pr-10 text-[14px] text-gray-700 focus:outline-none focus:border-tipico-red transition-colors cursor-pointer">
                <option value="">{t('account.limits.chooseOption')}</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
          </div>
        </div>

        <button className="w-full bg-[#e8e9eb] hover:bg-gray-200 text-gray-900 font-bold text-[14px] py-3.5 rounded-lg transition-colors cursor-pointer border border-[#d1d5db]">
          {t('account.limits.edit')}
        </button>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div className="flex flex-col">
      {renderHeader('account.limits.historyTitle')}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button 
          onClick={() => setHistoryTab('lugas')}
          className={`pb-3 px-4 font-bold text-[14px] transition-colors cursor-pointer ${historyTab === 'lugas' ? 'border-b-2 border-tipico-red text-tipico-red' : 'text-gray-500 hover:text-gray-900 border-b-2 border-transparent'}`}>
          {t('account.limits.tabLugas')}
        </button>
        <button 
          onClick={() => setHistoryTab('sports')}
          className={`pb-3 px-4 font-bold text-[14px] transition-colors cursor-pointer ${historyTab === 'sports' ? 'border-b-2 border-tipico-red text-tipico-red' : 'text-gray-500 hover:text-gray-900 border-b-2 border-transparent'}`}>
          {t('account.limits.tabSports')}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-5 flex items-start justify-between hover:bg-gray-50 transition-colors">
          <div className="flex flex-col gap-1">
            <span className="font-bold text-[14px] text-gray-900">{t('account.limits.monthlyDeposit')}</span>
            <span className="text-[12px] text-gray-400">25.08.2026, 00:22</span>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="font-black text-[14px] text-gray-900">100 €</span>
            <span className="text-[12px] text-gray-400">25.08.2026, 00:22</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full bg-white md:bg-transparent rounded md:rounded-none overflow-hidden p-0 md:p-2 max-w-[800px] mx-auto">
      <div className="bg-transparent md:bg-white rounded overflow-hidden">
        <div className="p-4 md:p-8">
          {view === 'menu' && renderMenu()}
          {view === 'limits' && renderLimits()}
          {view === 'closure' && renderClosure()}
          {view === 'history' && renderHistory()}
        </div>
      </div>
    </div>
  );
};
